import { cn } from "@base/lib/utils";
import useIntersectionObserver from "@base/model/hooks/useIntersectionObserver";
import { AppLink } from "@base/ui/AppLink";
import { Button } from "@buildblockinc/buildblock-design-system";
import { AlertCircle, RefreshCw } from "lucide-react";
import { useEffect, useMemo } from "react";
import { type DailyLogSearchErrorKind, isCursorErrorKind } from "../model/dailyLogSearchErrors";
import type { DailyLogSearchReturnState } from "../model/dailyLogSearchNavigation";
import type { DailyLogSearchItem } from "../model/dailyLogSearchSchema";
import type { DailyLogSearchResult } from "../model/useDailyLogSearch";
import { DailyLogSearchRow } from "./DailyLogSearchRow";

export type ResultOpenMethod = "same_tab" | "new_tab";
export type ResultOpenHandler = (item: DailyLogSearchItem, position: number, openMethod: ResultOpenMethod) => void;

const SKELETON_ROW_COUNT = 5;

interface DailyLogSearchListProps {
  projectId: number;
  search: DailyLogSearchResult;
  hasKeyword: boolean;
  hasConditions: boolean;
  onResetAll: () => void;
  /** 상세로 넘기는 복귀 정보 (history state) */
  linkState: DailyLogSearchReturnState;
  onResultOpen: ResultOpenHandler;
  /** 행 메뉴의 Edit로 목록을 떠날 때. 결과 열람 계측과 구분한다 */
  onEditOpen: (item: DailyLogSearchItem) => void;
  /** 캐시 없는 복원이 실패했을 때 처음부터 보기 */
  onAbandonRestore: () => void;
}

export function DailyLogSearchList(props: DailyLogSearchListProps) {
  const { search, onAbandonRestore } = props;
  // 권한이 없어졌으면 이전에 받은 행도 더 보여 주지 않는다.
  if (search.isAccessDenied) {
    return <SearchErrorState kind={search.errorKind ?? "forbidden"} onRetry={search.refresh} />;
  }

  switch (search.status) {
    case "idle":
      return null;
    case "loading":
      return <SearchLoadingState isPaused={search.isPaused} />;
    case "restoring":
      return (
        <SearchRestoringState
          errorKind={search.restoreErrorKind}
          onRetry={search.retryRestore}
          onStartOver={onAbandonRestore}
        />
      );
    case "error":
      return <SearchErrorState kind={search.errorKind ?? "server"} onRetry={search.refresh} />;
    case "success":
      return <SearchResults {...props} />;
  }
}

function SearchResults({
  projectId,
  search,
  hasKeyword,
  hasConditions,
  onResetAll,
  linkState,
  onResultOpen,
  onEditOpen,
}: DailyLogSearchListProps) {
  const { items, count, isUpdating, isRefreshing } = search;

  return (
    <section aria-label="Daily logs" aria-busy={isUpdating || isRefreshing}>
      <div className="flex gap-2 justify-between items-center py-2">
        <p className={cn("text-label3 text-gray-800 md:text-label2", isUpdating && "text-gray-500")} aria-live="polite">
          {isUpdating ? "Updating results…" : formatCount(count)}
        </p>
        <Button
          type="button"
          variant="ghost"
          color="secondary"
          size="small"
          onClick={search.refresh}
          disabled={isRefreshing || isUpdating}
          prefixIcon={
            <RefreshCw width={14} height={14} aria-hidden="true" className={cn(isRefreshing && "animate-spin")} />
          }
        >
          Refresh results
        </Button>
      </div>

      {search.isPaused && (
        <output className="block py-2 text-label3 text-gray-700">
          You're offline. Showing the last loaded results.
        </output>
      )}

      {search.refreshErrorKind && (
        <InlineNotice onAction={search.refresh} actionLabel="Try again">
          {getErrorMessage(search.refreshErrorKind)}
        </InlineNotice>
      )}

      {items.length === 0 ? (
        <EmptyState projectId={projectId} hasConditions={hasConditions} onResetAll={onResetAll} />
      ) : (
        <>
          <ul className="flex flex-col gap-2 md:gap-0">
            {items.map((item, index) => (
              <DailyLogSearchRow
                key={item.id}
                projectId={projectId}
                item={item}
                hasKeyword={hasKeyword}
                disabled={isUpdating}
                linkState={linkState}
                onOpen={(openMethod) => onResultOpen(item, index + 1, openMethod)}
                onEditOpen={() => onEditOpen(item)}
              />
            ))}
          </ul>
          <LoadMoreFooter search={search} />
        </>
      )}
    </section>
  );
}

function formatCount(count: number) {
  return count === 1 ? "1 daily log" : `${count.toLocaleString()} daily logs`;
}

function LoadMoreFooter({ search }: { search: DailyLogSearchResult }) {
  const { canLoadMore, loadMore, nextPageErrorKind, isFetchingNextPage, hasNextPage } = search;
  const observerOptions = useMemo(() => ({ rootMargin: "200px" }), []);
  const { ref: sentinelRef, isIntersecting } = useIntersectionObserver(observerOptions);

  // 후속 조회가 실패하면 자동 재요청하지 않는다. 사용자가 버튼으로 재시도한다.
  const shouldAutoLoad = isIntersecting && canLoadMore && nextPageErrorKind === null;

  useEffect(
    function loadNextPageWhenSentinelVisible() {
      if (shouldAutoLoad) loadMore();
    },
    [shouldAutoLoad, loadMore],
  );

  if (nextPageErrorKind !== null) {
    if (isCursorErrorKind(nextPageErrorKind)) {
      return (
        <InlineNotice onAction={search.refresh} actionLabel="Refresh results">
          {getErrorMessage(nextPageErrorKind)}
        </InlineNotice>
      );
    }
    return (
      <InlineNotice onAction={loadMore} actionLabel="Try again">
        {getErrorMessage(nextPageErrorKind)}
      </InlineNotice>
    );
  }

  if (!hasNextPage) return null;

  return (
    <div className="flex flex-col items-center py-4">
      <div ref={sentinelRef as (instance: HTMLDivElement | null) => void} className="h-px w-full" />
      {isFetchingNextPage ? (
        <output className="flex gap-2 items-center text-label3 text-gray-700">
          <span className="w-5 h-5 rounded-full border-2 border-gray-300 animate-spin border-t-gray-600" />
          Loading more…
        </output>
      ) : (
        <Button
          type="button"
          variant="outline"
          color="secondary"
          size="small"
          onClick={loadMore}
          disabled={!canLoadMore}
        >
          Load more
        </Button>
      )}
    </div>
  );
}

interface EmptyStateProps {
  projectId: number;
  hasConditions: boolean;
  onResetAll: () => void;
}

function EmptyState({ projectId, hasConditions, onResetAll }: EmptyStateProps) {
  if (hasConditions) {
    return (
      <div className="flex flex-col gap-3 items-center py-10 text-center">
        <p className="text-body3 text-gray-900">No daily logs match these filters.</p>
        <p className="text-body4 text-gray-700">Try a different keyword or remove some filters.</p>
        <Button type="button" variant="outline" color="secondary" size="small" onClick={onResetAll}>
          Reset all
        </Button>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-3 items-center py-10 text-center">
      <p className="text-body3 text-gray-900">No daily logs yet.</p>
      <AppLink
        to="/projects/$projectId/log/new"
        params={{ projectId: String(projectId) }}
        className="px-4 py-2 text-white bg-blue-500 rounded-8 text-label2"
      >
        Write a daily log
      </AppLink>
    </div>
  );
}

function SearchLoadingState({ isPaused }: { isPaused: boolean }) {
  return (
    <div aria-busy="true">
      {isPaused && (
        <output className="block py-2 text-label2 text-center text-gray-700">
          You're offline. Results will load when you reconnect.
        </output>
      )}
      <ul className="flex flex-col">
        {Array.from({ length: SKELETON_ROW_COUNT }, (_, i) => `skeleton-${i}`).map((key) => (
          <li key={key} className="flex flex-col gap-2 py-3 border-b border-gray-200">
            <div className="w-32 h-4 rounded-2 skeleton-shimmer" />
            <div className="w-full h-3 rounded-2 skeleton-shimmer" />
            <div className="w-2/3 h-3 rounded-2 skeleton-shimmer" />
          </li>
        ))}
      </ul>
    </div>
  );
}

interface SearchRestoringStateProps {
  errorKind: DailyLogSearchErrorKind | null;
  onRetry: () => void;
  onStartOver: () => void;
}

function SearchRestoringState({ errorKind, onRetry, onStartOver }: SearchRestoringStateProps) {
  if (errorKind === null) {
    return (
      <div aria-busy="true">
        <output className="block py-2 text-label2 text-gray-700">Restoring your place in the list…</output>
        <ul className="flex flex-col">
          {Array.from({ length: SKELETON_ROW_COUNT }, (_, i) => `restoring-skeleton-${i}`).map((key) => (
            <li key={key} className="flex flex-col gap-2 py-3 border-b border-gray-200">
              <div className="w-32 h-4 rounded-2 skeleton-shimmer" />
              <div className="w-full h-3 rounded-2 skeleton-shimmer" />
            </li>
          ))}
        </ul>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-3 items-center py-10 text-center" role="alert">
      <p className="text-body4 text-gray-800">{`Couldn't restore your place in the list. ${getErrorMessage(errorKind)}`}</p>
      <div className="flex gap-2">
        <Button type="button" variant="outline" color="secondary" size="small" onClick={onRetry}>
          Try again
        </Button>
        <Button type="button" variant="ghost" color="secondary" size="small" onClick={onStartOver}>
          Start from the top
        </Button>
      </div>
    </div>
  );
}

const RETRYABLE_ERROR_KINDS: ReadonlySet<DailyLogSearchErrorKind> = new Set([
  "not-ready",
  "network",
  "server",
  "rate-limited",
  "canceled",
]);

function SearchErrorState({ kind, onRetry }: { kind: DailyLogSearchErrorKind; onRetry: () => void }) {
  return (
    <div className="flex flex-col gap-3 items-center py-10 text-center text-gray-700" role="alert">
      <AlertCircle width={32} height={32} aria-hidden="true" className="text-gray-400" />
      <p className="text-body4">{getErrorMessage(kind)}</p>
      {RETRYABLE_ERROR_KINDS.has(kind) && (
        <Button type="button" variant="outline" color="secondary" size="small" onClick={onRetry}>
          Try again
        </Button>
      )}
    </div>
  );
}

interface InlineNoticeProps {
  children: React.ReactNode;
  actionLabel: string;
  onAction: () => void;
}

function InlineNotice({ children, actionLabel, onAction }: InlineNoticeProps) {
  return (
    <div className="flex flex-wrap gap-2 justify-between items-center p-3 my-2 bg-gray-100 rounded-8" role="alert">
      <p className="text-body4 text-gray-800">{children}</p>
      <Button type="button" variant="outline" color="secondary" size="small" onClick={onAction}>
        {actionLabel}
      </Button>
    </div>
  );
}

/** 서버 message 대신 code 기반의 고정 문구를 쓴다 */
function getErrorMessage(kind: DailyLogSearchErrorKind): string {
  switch (kind) {
    case "invalid-request":
      return "Some search conditions are invalid. Adjust the filters and try again.";
    case "cursor-expired":
      return "These results have expired. Refresh results to continue.";
    case "cursor-invalid":
      return "These results are out of date. Refresh results to continue.";
    case "forbidden":
      return "You don't have access to this project's daily logs.";
    case "unauthorized":
      return "Your session has expired. Please sign in again.";
    case "rate-limited":
      return "Too many requests. Please wait a moment and try again.";
    case "not-ready":
      return "Daily log search is being prepared. Please try again shortly.";
    case "network":
      return "Couldn't connect. Check your connection and try again.";
    case "canceled":
    case "server":
      return "Failed to load daily logs.";
  }
}
